// src/components/PortalVerificacao/SecaoDocumentos.tsx
// Lista dos documentos enviados pelo usuário no cadastro
// CREATED: 2026-05-07 — v1.0

import React, { useState } from 'react';
import { FileText, Image, File, ExternalLink, Eye, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { DocumentoVerificacao } from '../../services/verificacaoService';

const BRAND = { gold: '#D4AF37', goldDeep: '#B88917', charcoal: '#1F2328' };

const DOC_TYPE_LABELS: Record<string, string> = {
  diploma:         'Diploma',
  cpf:             'CPF',
  rg:              'RG / Identidade',
  foto:            'Foto',
  comprovante:     'Comprovante de Residência',
  certificado:     'Certificado',
  outros:          'Outros',
};

interface Props { documentos: DocumentoVerificacao[]; }

const getIconForMime = (mime: string) => {
  if (mime?.startsWith('image/')) return <Image className="w-5 h-5" style={{ color: '#B88917' }} />;
  if (mime === 'application/pdf')  return <FileText className="w-5 h-5 text-red-500" />;
  return <File className="w-5 h-5 text-gray-400" />;
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const SecaoDocumentos: React.FC<Props> = ({ documentos }) => {
  const [expandido, setExpandido] = useState(true);
  const [preview,   setPreview]   = useState<DocumentoVerificacao | null>(null);

  if (!documentos || documentos.length === 0) {
    return (
      <div className="rounded-3xl border bg-white p-6 shadow-sm" style={{ borderColor: `${BRAND.gold}33` }}>
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-gray-300" />
          <div>
            <h3 className="text-base font-black text-gray-400">Documentos</h3>
            <p className="text-sm text-gray-400">Nenhum documento encontrado neste protocolo.</p>
          </div>
        </div>
      </div>
    );
  }

  // Agrupar por tipo
  const agrupados = documentos.reduce<Record<string, DocumentoVerificacao[]>>((acc, doc) => {
    const key = doc.documentType || 'outros';
    if (!acc[key]) acc[key] = [];
    acc[key].push(doc);
    return acc;
  }, {});

  return (
    <>
      <div className="rounded-3xl border bg-white p-6 shadow-sm" style={{ borderColor: `${BRAND.gold}33` }}>
        {/* Cabeçalho clicável */}
        <button
          className="w-full flex items-center justify-between"
          onClick={() => setExpandido(!expandido)}
        >
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl" style={{ backgroundColor: `${BRAND.gold}1A` }}>
              <FileText className="w-6 h-6" style={{ color: BRAND.goldDeep }} />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-black" style={{ color: BRAND.charcoal }}>Documentos Enviados</h3>
              <p className="text-sm text-gray-500">{documentos.length} arquivo{documentos.length !== 1 ? 's' : ''} enviado{documentos.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {expandido
            ? <ChevronUp className="w-5 h-5 text-gray-400" />
            : <ChevronDown className="w-5 h-5 text-gray-400" />
          }
        </button>

        {expandido && (
          <div className="mt-5 space-y-5">
            {Object.entries(agrupados).map(([tipo, docs]) => (
              <div key={tipo}>
                <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">
                  {DOC_TYPE_LABELS[tipo] || tipo} ({docs.length})
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {docs.map(doc => {
                    const isImagem = doc.mimeType?.startsWith('image/');
                    return (
                      <div
                        key={doc.id}
                        className="rounded-2xl border overflow-hidden hover:shadow-md transition"
                        style={{ borderColor: `${BRAND.gold}33` }}
                      >
                        {/* Preview imagem */}
                        {isImagem && (
                          <button
                            onClick={() => setPreview(doc)}
                            className="w-full block overflow-hidden"
                            style={{ height: '120px', backgroundColor: '#f3f4f6' }}
                          >
                            <img
                              src={doc.url}
                              alt={doc.description || doc.fileName}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                            />
                          </button>
                        )}

                        {/* Info */}
                        <div className="p-3 flex items-center gap-3">
                          <div className="flex-shrink-0">{getIconForMime(doc.mimeType)}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold truncate" style={{ color: BRAND.charcoal }}>
                              {doc.description || doc.fileName}
                            </p>
                            <p className="text-xs text-gray-400">{formatBytes(doc.fileSize)}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            {isImagem && (
                              <button
                                onClick={() => setPreview(doc)}
                                className="p-1.5 rounded-xl hover:bg-gray-100 transition"
                                title="Visualizar"
                              >
                                <Eye className="w-4 h-4 text-gray-500" />
                              </button>
                            )}
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded-xl hover:bg-gray-100 transition"
                              title="Abrir"
                            >
                              <ExternalLink className="w-4 h-4 text-gray-500" />
                            </a>
                            <a
                              href={doc.url}
                              download={doc.fileName}
                              className="p-1.5 rounded-xl hover:bg-gray-100 transition"
                              title="Baixar"
                            >
                              <Download className="w-4 h-4 text-gray-500" />
                            </a>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox de preview */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
          onClick={() => setPreview(null)}
        >
          <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <img
              src={preview.url}
              alt={preview.description || preview.fileName}
              className="w-full rounded-2xl shadow-2xl"
              style={{ maxHeight: '80vh', objectFit: 'contain' }}
            />
            <button
              onClick={() => setPreview(null)}
              className="absolute top-3 right-3 bg-black/60 text-white rounded-full px-4 py-2 text-sm font-bold hover:bg-black/80 transition"
            >
              Fechar
            </button>
            <div className="mt-3 text-center text-white text-sm font-medium">
              {preview.description || preview.fileName}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SecaoDocumentos;